import React from 'react';

import { postJSON } from '../../api/client.js';
import {
  CrudPage, UniActionButton, UniPageShell, UniSection, UniStatusPill, UniTable, useUniList,
} from '../waba/WabaShared.jsx';
import { UniCopyField } from '../uni/UniShared.jsx';
import {
  EasyField, ExistingPicker, PixelTestAside, SB_API, THEME, HOME, TechDetails,
  WizardShell, useWizard, CustomShopifyAppCard, ShopifyAppInstallCard, ShopifyConnectCodeCard,
  OrderSyncHoursField, DEFAULT_MANUAL_ORDER_SYNC_HOURS, runStoreOrderSync, OrderSyncStatus,
} from './SignalBridgeShared.jsx';
import { STORE_FIELD_HELP } from './storeFieldHelp.js';

const P = { theme: THEME, api: SB_API, home: HOME };

export function SignalBridgePixelWizard({ embedded, onFinished, forceRef }) {
  const { state, busy, data, setField, act } = useWizard('pixel', { onFinished, forceRef });
  const step = state?.current_step || 1;
  const groups = useUniList(`${SB_API}/pixel-groups/`);
  const savedPk = state?.created?.pixel_id || data.pixel_pk || '';
  const hasToken = !!(data.access_token || data.keep_existing_token || savedPk);
  const canAttempt = !!(data.pixel_id && hasToken);
  const testResult = (state?.test_send || state?.validation)
    ? { validation: state.validation, test_send: state.test_send }
    : null;
  const editing = state?.mode === 'edit' || !!data.pixel_pk;
  return (
    <WizardShell
      embedded={embedded}
      title={editing ? 'Edit Meta pixel' : 'Add a Meta pixel'}
      subtitle="Pixel ID and access token from Events Manager. Graph checks the token before the next screen. Test CAPI from the panel on the right — it does not edit the pixel."
      helpPage="wizard_pixel"
      state={state}
      busy={busy}
      act={act}
      aside={(
        <PixelTestAside
          wizardAct={act}
          busy={busy}
          result={testResult}
          canAttempt={canAttempt}
          blockedReason="Enter a Pixel ID and CAPI token first. After Finish you can keep testing here without editing the pixel."
        />
      )}
    >
      <ExistingPicker
        items={state?.existing?.pixels}
        value={data.pixel_pk}
        onSelect={(id) => act('load_existing', { pixel_pk: id })}
        onCreateNew={() => act('start_new')}
        label="Previously saved pixels"
      />
      {step === 1 && (
        <div>
          <EasyField label="Name" hint="Anything you’ll recognise later — e.g. Main shop pixel.">
            <input value={data.name || ''} onChange={(e) => setField('name', e.target.value)} />
          </EasyField>
          <EasyField label="Pixel ID" hint="Events Manager → Data sources → your pixel’s numeric ID. Each Pixel ID can only be added once.">
            <input value={data.pixel_id || ''} onChange={(e) => setField('pixel_id', e.target.value)} />
          </EasyField>
          <EasyField label="Shop URL (optional)" hint="Used only when an event has no landing page.">
            <input value={data.default_event_source_url || ''} onChange={(e) => setField('default_event_source_url', e.target.value)} placeholder="https://yourshop.com" />
          </EasyField>
        </div>
      )}
      {step === 2 && (
        <div>
          <p className="uni-num--muted">{state.help?.token || 'Paste a Conversions API token from Events Manager → Settings → Generate access token.'}</p>
          {data.pixel_pk && (
            <EasyField label="Keep the token already saved">
              <input type="checkbox" checked={data.keep_existing_token !== false} onChange={(e) => setField('keep_existing_token', e.target.checked)} />
            </EasyField>
          )}
          <EasyField label="Access token" hint={data.token_set ? 'Leave blank to keep the stored token. Continue still checks it with Meta.' : ''}>
            <input type="password" placeholder={data.token_set ? '•••••••• (leave blank to keep)' : 'CAPI access token'} value={data.access_token || ''} onChange={(e) => setField('access_token', e.target.value)} />
          </EasyField>
          <TechDetails title="Token check" data={state.validation} />
        </div>
      )}
      {step === 3 && (
        <div>
          <p>{state.created ? `Saved ${state.created.name}. Use the testing panel beside this form — it does not change the pixel record.` : 'Finish saves the pixel and starts sending matching events to Meta. Test CAPI from the panel on the right.'}</p>
          <p className="uni-num--muted">{state.help?.test_aside}</p>
          <EasyField label="Add to a pixel group (optional)">
            <select value={data.pixel_group_id || ''} onChange={(e) => setField('pixel_group_id', e.target.value)}>
              <option value="">— none —</option>
              {(groups.rows || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </EasyField>
        </div>
      )}
    </WizardShell>
  );
}

export function SignalBridgeStoreWizard({ embedded, onFinished, forceRef }) {
  const { state, busy, data, setField, act } = useWizard('store', { onFinished, forceRef });
  const step = state?.current_step || 1;
  const pixels = useUniList(`${SB_API}/pixels/`);
  const editing = state?.mode === 'edit' || !!data.store_id;
  const [syncBusy, setSyncBusy] = React.useState(false);
  const [orderSync, setOrderSync] = React.useState(null);

  const runWizardOrderSync = async () => {
    const storeId = data.store_id;
    const hours = Number(data.hours) || DEFAULT_MANUAL_ORDER_SYNC_HOURS;
    if (!storeId) {
      setOrderSync({ ok: false, message: 'Save the store first, then sync orders.' });
      return;
    }
    setSyncBusy(true);
    setOrderSync({
      ok: true,
      started: true,
      status: 'running',
      message: 'Starting order sync…',
    });
    try {
      const result = await runStoreOrderSync(storeId, hours, { onProgress: setOrderSync });
      setOrderSync(result);
    } catch (err) {
      const payload = err && err.data;
      setOrderSync({
        ok: false,
        error: String(err.message || err),
        detail: payload && (payload.detail || payload.message),
        ...((payload && typeof payload === 'object') ? payload : {}),
      });
    } finally {
      setSyncBusy(false);
    }
  };
  return (
    <WizardShell
      embedded={embedded}
      title={editing ? 'Edit store connection' : 'Connect a store'}
      subtitle="Install SignalBridge on Shopify, or connect GoKwik. Continue checks shop.json when an Admin token is present."
      helpPage="wizard_store"
      state={state}
      busy={busy}
      act={act}
    >
      <ExistingPicker
        items={state?.existing?.stores}
        value={data.store_id}
        onSelect={(id) => act('load_existing', { store_id: id })}
        onCreateNew={() => act('start_new')}
        label="Previously saved stores"
      />
      <ShopifyAppInstallCard />
      <ShopifyConnectCodeCard
        later
        autoRedeem
        onConnected={(res) => {
          if (res && res.shop) setField('store_domain', res.shop);
        }}
      />
      <CustomShopifyAppCard
        onConnected={(res) => {
          if (res && res.store_domain) setField('store_domain', res.store_domain);
          if (res && res.public_domain) setField('public_domain', res.public_domain);
        }}
      />
      {step === 1 && (
        <EasyField label="Platform" hint="Pick the checkout that actually takes the order." help={STORE_FIELD_HELP.platform}>
          <select value={data.platform || ''} onChange={(e) => setField('platform', e.target.value)}>
            <option value="">Choose…</option>
            <option value="SHOPIFY">Shopify</option>
            <option value="GOKWIK">GoKwik</option>
          </select>
        </EasyField>
      )}
      {step === 2 && (
        <div>
          <EasyField label="Meta pixel (optional)" hint="Leave empty if you only want Google Ads for now." help={STORE_FIELD_HELP.pixel_account}>
            <select value={data.pixel_account || ''} onChange={(e) => setField('pixel_account', e.target.value)}>
              <option value="">— none yet —</option>
              {(pixels.rows || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </EasyField>
          <EasyField label="Store domain" hint="janayush.myshopify.com or your GoKwik shop domain." help={STORE_FIELD_HELP.store_domain}>
            <input placeholder="your-store.myshopify.com" value={data.store_domain || ''} onChange={(e) => setField('store_domain', e.target.value)} />
          </EasyField>
          <EasyField label="Customer-facing URL" hint="https://yourshop.com — used when an event has no landing page." help={STORE_FIELD_HELP.public_domain}>
            <input placeholder="https://yourshop.com" value={data.public_domain || ''} onChange={(e) => setField('public_domain', e.target.value)} />
          </EasyField>
          {data.store_id && (
            <EasyField label="Keep the webhook secret already saved">
              <input type="checkbox" checked={data.keep_existing_secret !== false} onChange={(e) => setField('keep_existing_secret', e.target.checked)} />
            </EasyField>
          )}
          <EasyField label="Webhook secret" hint={data.webhook_secret_set ? 'Leave blank to keep the stored secret.' : 'Shopify notification signing secret, or the GoKwik HMAC secret.'} help={STORE_FIELD_HELP.webhook_secret}>
            <input type="password" placeholder={data.webhook_secret_set ? '•••••••• (leave blank to keep)' : ''} value={data.webhook_secret || ''} onChange={(e) => setField('webhook_secret', e.target.value)} />
          </EasyField>
          {data.platform === 'SHOPIFY' && (
            <>
              <p className="uni-num--muted">
                Prefer Connect this Shopify store above (Client ID + Client secret
                from this store’s Dev Dashboard). Paste a static Admin token only
                for a legacy custom app created before 2026.
              </p>
              <EasyField label="Shopify Admin API token (optional)" hint={data.api_token_set ? 'Leave blank to keep. Continue still calls shop.json to verify the shop.' : 'Skip if you connected with Client ID and Client secret.'} help={STORE_FIELD_HELP.api_access_token}>
                <input type="password" placeholder={data.api_token_set ? '•••••••• (leave blank to keep)' : ''} value={data.api_access_token || ''} onChange={(e) => setField('api_access_token', e.target.value)} />
              </EasyField>
            </>
          )}
          {data.platform === 'GOKWIK' && (
            <>
              <EasyField label="GoKwik App ID" hint="Dashboard → Account. Needed to pull orders when a webhook is missed." help={STORE_FIELD_HELP.gokwik_app_id}>
                <input
                  placeholder={data.gokwik_app_id_set ? '•••• (leave blank to keep)' : 'App ID'}
                  value={data.gokwik_app_id || ''}
                  onChange={(e) => setField('gokwik_app_id', e.target.value)}
                />
              </EasyField>
              <EasyField label="GoKwik App Secret" hint={data.api_token_set ? 'Leave blank to keep the stored secret.' : 'Dashboard → Account → App Secret.'} help={STORE_FIELD_HELP.api_access_token}>
                <input type="password" placeholder={data.api_token_set ? '•••••••• (leave blank to keep)' : ''} value={data.api_access_token || ''} onChange={(e) => setField('api_access_token', e.target.value)} />
              </EasyField>
              <EasyField label="Auth mode" hint="How GoKwik signs inbound webhooks. HMAC is recommended." help={STORE_FIELD_HELP.auth_mode}>
                <select value={data.auth_mode || 'HMAC'} onChange={(e) => setField('auth_mode', e.target.value)}>
                  <option value="HMAC">HMAC</option>
                  <option value="SHARED_TOKEN">Shared token</option>
                  <option value="IP_ALLOWLIST">IP allow-list</option>
                </select>
              </EasyField>
              <EasyField label="Shared token" hint="Only if GoKwik is not using HMAC." help={STORE_FIELD_HELP.shared_token}>
                <input type="password" placeholder={data.shared_token_set ? '•••••••• (leave blank to keep)' : ''} value={data.shared_token || ''} onChange={(e) => setField('shared_token', e.target.value)} />
              </EasyField>
            </>
          )}
          <TechDetails title="Shop.json check" data={state.shop} />
        </div>
      )}
      {step === 3 && (
        <div>
          <p className="uni-num--muted">
            Copy the Shopify URL into the Shopify script. Copy the GoKwik URL into GoKwik Dashboard → Kwik Checkout → Settings → Integrations → Webhooks (orders, Abandoned Cart Address Added, and Payment Webhooks V3). They are different paths.
          </p>
          <UniCopyField
            label="Shopify webhook URL"
            open={false}
            hint="Copy into the Shopify script, or use Register webhooks. Shopify signs this URL."
            value={(state.webhook_urls || data.webhook_urls || {}).shopify || ''}
          />
          <UniCopyField
            label="GoKwik webhook URL"
            open={false}
            hint="Copy into GoKwik. Same URL for Abandoned Cart (Address Added) and Payment Webhooks V3. No auth header."
            value={(state.webhook_urls || data.webhook_urls || {}).gokwik || ''}
          />
          <TechDetails title="All URL variants" data={state.webhook_urls || data.webhook_urls} />
          {data.platform === 'SHOPIFY' && <UniActionButton hero onClick={() => act('register')}>Register webhooks now</UniActionButton>}
          <TechDetails title="Registration result" data={state.registration} />
        </div>
      )}
      {step === 4 && (
        <div>
          <p>
            {data.platform === 'GOKWIK'
              ? 'Place a test GoKwik order, or sync recent orders if App ID and App Secret are saved. This page polls when you click Check now.'
              : 'Place a test order (or fire a Shopify webhook test). This page polls when you click Check now.'}
          </p>
          {(data.platform === 'SHOPIFY' || data.platform === 'GOKWIK') && (
            <OrderSyncHoursField
              value={Number(data.hours) || DEFAULT_MANUAL_ORDER_SYNC_HOURS}
              onChange={(hours) => setField('hours', hours)}
            />
          )}
          <UniActionButton hero primary onClick={() => act('listen')}>Check now</UniActionButton>
          {' '}
          {data.platform === 'SHOPIFY' && (
            <UniActionButton hero disabled={syncBusy} onClick={runWizardOrderSync}>
              {syncBusy ? 'Syncing…' : 'Sync orders from Shopify'}
            </UniActionButton>
          )}
          {data.platform === 'GOKWIK' && (
            <UniActionButton hero disabled={syncBusy} onClick={runWizardOrderSync}>
              {syncBusy ? 'Syncing…' : 'Sync orders from GoKwik'}
            </UniActionButton>
          )}
          {' '}
          <UniActionButton hero onClick={() => act('complete', { skip_listen: true })}>Skip for now</UniActionButton>
          {state.created?.needs_first_event_badge && <p>This store has no inbound event yet — a warning stays until the first order arrives.</p>}
          <TechDetails title="Listener" data={state.listening} />
          <OrderSyncStatus data={orderSync || state.order_sync} />
        </div>
      )}
    </WizardShell>
  );
}

export function SignalBridgeEventsWizard({ embedded, onFinished, forceRef }) {
  const { state, busy, data, setField, act } = useWizard('events', { onFinished, forceRef });
  const step = state?.current_step || 1;
  const pixels = useUniList(`${SB_API}/pixels/`);
  const selected = new Set(data.event_types || ['Purchase']);
  const toggleEvent = (et) => {
    const next = new Set(selected);
    if (next.has(et)) next.delete(et); else next.add(et);
    setField('event_types', [...next]);
  };
  const params = data.parameters || {};
  const toggleParam = (et, key) => {
    const list = new Set(params[et] || []);
    if (list.has(key)) list.delete(key); else list.add(key);
    setField('parameters', { ...params, [et]: [...list] });
  };
  const fields = state?.availability?.fields || {};
  const pixelOpts = (state?.existing?.pixels || pixels.rows || []).map((p) => ({
    id: p.id, label: p.label || p.name,
  }));
  return (
    <WizardShell
      embedded={embedded}
      title="Which events to send"
      subtitle="Turn on Purchase, AddToCart and the rest. We’ll only send fields that actually arrived from the store. Pick a pixel you already saved to edit its matrix."
      helpPage="wizard_events"
      state={state}
      busy={busy}
      act={act}
    >
      <ExistingPicker
        items={pixelOpts}
        value={data.pixel_id}
        onSelect={(id) => act('load_existing', { pixel_id: id })}
        onCreateNew={() => act('start_new')}
        label="Previously saved pixels"
        createLabel="Choose a pixel below"
      />
      {step === 1 && (
        <div>
          <EasyField label="Pixel">
            <select
              value={data.pixel_id || ''}
              onChange={(e) => {
                const id = e.target.value;
                setField('pixel_id', id);
                if (id) act('load_existing', { pixel_id: id });
              }}
            >
              <option value="">Choose…</option>
              {pixelOpts.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </EasyField>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginTop: 12 }}>
            {(state?.event_cards || []).map((c) => (
              <label key={c.event_type} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
                <input type="checkbox" checked={selected.has(c.event_type)} onChange={() => toggleEvent(c.event_type)} /> <strong>{c.label}</strong>
                <div className="uni-num--muted">{c.blurb}</div>
              </label>
            ))}
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          {(data.event_types || ['Purchase']).map((et) => (
            <UniSection key={et} title={et}>
              <UniTable
                rows={Object.entries(fields).map(([key, meta]) => ({ key, ...meta }))}
                rowKey="key"
                empty="No parameters"
                columns={[
                  { key: 'key', label: 'Field' },
                  { key: 'group', label: 'Group' },
                  { key: 'description', label: 'Meaning' },
                  { key: 'badge', label: 'Last 30 days' },
                  {
                    key: 'on',
                    label: 'Send',
                    render: (r) => r.always_on
                      ? <input type="checkbox" checked disabled />
                      : <input type="checkbox" checked={(params[et] || []).includes(r.key) || r.always_on} onChange={() => toggleParam(et, r.key)} />,
                  },
                ]}
              />
            </UniSection>
          ))}
        </div>
      )}
      {step === 3 && (
        <div>
          <UniActionButton hero onClick={() => act('preview')}>Refresh preview</UniActionButton>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div>
              <h4>From your store</h4>
              <TechDetails title="Received" data={state.preview?.source || {}} />
            </div>
            <div>
              <h4>Sent to ads {state.preview?.hashed_before_sending?.length ? `(hashed: ${state.preview.hashed_before_sending.join(', ')})` : ''}</h4>
              <TechDetails title="Will send" data={state.preview?.will_send || {}} />
            </div>
          </div>
        </div>
      )}
      {step === 4 && (
        <div>
          <p className="uni-num--muted">Paste a Test Events code for this send only. It is not saved on the pixel.</p>
          <EasyField label="Test event code">
            <input placeholder="TEST12345" value={data.test_event_code || ''} onChange={(e) => setField('test_event_code', e.target.value)} />
          </EasyField>
          <UniActionButton hero onClick={() => act('verify')}>Send a test</UniActionButton>
          {state.created && <p>Saved {state.created.updated} parameter rows.</p>}
          <TechDetails title="Test send" data={state.test_send} />
        </div>
      )}
    </WizardShell>
  );
}

export function SignalBridgeMergeWizard({ embedded, onFinished, forceRef }) {
  const { state, busy, data, setField, act } = useWizard('merge', { onFinished, forceRef });
  const step = state?.current_step || 1;
  const stores = state?.stores || [];
  const shopify = stores.filter((s) => s.platform === 'SHOPIFY');
  const gokwik = stores.filter((s) => s.platform === 'GOKWIK');
  const prec = data.field_precedence || state?.defaults?.field_precedence || {};
  const setPrec = (field, side) => setField('field_precedence', { ...prec, [field]: side });
  const editing = state?.mode === 'edit' || !!data.merge_config_id;
  return (
    <WizardShell
      embedded={embedded}
      title={editing ? 'Edit merge pair' : 'Shopify + GoKwik as one sale'}
      subtitle="If checkout is split across Shopify and GoKwik, wait for both sides then send a single event."
      helpPage="wizard_merge"
      state={state}
      busy={busy}
      act={act}
    >
      <ExistingPicker
        items={state?.existing?.merge_configs}
        value={data.merge_config_id}
        onSelect={(id) => act('load_existing', { merge_config_id: id })}
        onCreateNew={() => act('start_new')}
        label="Previously saved merge pairs"
      />
      {step === 1 && (
        <div>
          <EasyField label="Shopify store">
            <select value={data.shopify_connection || ''} onChange={(e) => setField('shopify_connection', e.target.value)}>
              <option value="">Choose…</option>
              {shopify.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </EasyField>
          <EasyField label="GoKwik store">
            <select value={data.gokwik_connection || ''} onChange={(e) => setField('gokwik_connection', e.target.value)}>
              <option value="">Choose…</option>
              {gokwik.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </EasyField>
        </div>
      )}
      {step === 2 && (
        <div>
          <label><input type="checkbox" checked={data.use_shopify_order_id !== false} onChange={(e) => setField('use_shopify_order_id', e.target.checked)} /> Shopify order id (GoKwik merchant_order_id) — primary</label>
          <label><input type="checkbox" checked={data.use_checkout_token !== false} onChange={(e) => setField('use_checkout_token', e.target.checked)} /> Checkout token</label>
          <label>
            <input type="checkbox" checked={!!data.use_composite_fallback} onChange={(e) => setField('use_composite_fallback', e.target.checked)} /> Advanced: phone + value + ±30 min
          </label>
          {data.use_composite_fallback && <p className="ty-error">Composite matching can false-merge two different customers. Leave off unless you must.</p>}
        </div>
      )}
      {step === 3 && (
        <UniTable
          rows={Object.keys(prec).map((field) => ({ field, side: prec[field], why: (state.defaults?.rationale || {})[field] }))}
          rowKey="field"
          empty="No fields"
          columns={[
            { key: 'field', label: 'Field' },
            {
              key: 'side',
              label: 'Take from',
              render: (r) => (
                <select value={r.side} onChange={(e) => setPrec(r.field, e.target.value)}>
                  <option value="SHOPIFY">Shopify</option>
                  <option value="GOKWIK">GoKwik</option>
                </select>
              ),
            },
            { key: 'why', label: 'Why' },
          ]}
        />
      )}
      {step === 4 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <label>Merge window (seconds)
            <input type="number" value={data.merge_window_seconds || 900} onChange={(e) => setField('merge_window_seconds', Number(e.target.value))} />
          </label>
          <label>If the partner never arrives
            <select value={data.timeout_behavior || 'SEND_PARTIAL'} onChange={(e) => setField('timeout_behavior', e.target.value)}>
              <option value="SEND_PARTIAL">Send whichever side arrived</option>
              <option value="HOLD">Hold for a human</option>
            </select>
          </label>
        </div>
      )}
      {step === 5 && (
        <div>
          <UniActionButton hero onClick={() => act('rehearse')}>Find a recent overlapping order</UniActionButton>
          {state.rehearsal && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12 }}>
              <div><h4>Shopify</h4><TechDetails title="Shopify payload" data={state.rehearsal.shopify} /></div>
              <div><h4>GoKwik</h4><TechDetails title="GoKwik payload" data={state.rehearsal.gokwik} /></div>
              <div><h4>Merged</h4><TechDetails title="Merged event" data={state.rehearsal.merged} /></div>
            </div>
          )}
        </div>
      )}
    </WizardShell>
  );
}

export function SignalBridgeMergeBrowser() {
  const { raw, loading, error, reload } = useUniList(`${SB_API}/reports/?type=MERGE`);
  const groups = useUniList(`${SB_API}/merge-groups/`);
  const stores = useUniList(`${SB_API}/stores/`);
  const send = async (id) => {
    if (!window.confirm('Send the held/partial merge now? This creates one OutboundEvent.')) return;
    await postJSON(`${SB_API}/merge-groups/${id}/send-partial/`, {});
    reload && reload();
    groups.reload && groups.reload();
  };
  return (
    <UniPageShell {...P} icon="⚭" title="Merge" helpPage="merge" helpApi={SB_API} actions={<><UniActionButton hero primary to="/signalbridge/merge-setup">Set up merge</UniActionButton><UniActionButton hero to={HOME}>← Home</UniActionButton></>}>
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <div className="uni-kpis">
        <div>Merge rate {raw?.merge_rate_pct ?? '—'}%</div>
        <div>Timed out {raw?.timed_out ?? 0}</div>
        <div>Late partner {raw?.late_partner ?? 0}</div>
        <div>Avg wait {raw?.avg_wait_seconds ?? '—'}s</div>
      </div>
      <UniSection title="Groups" />
      <UniTable
        rows={groups.rows || raw?.groups || []}
        loading={groups.loading}
        rowKey="id"
        empty="No merge groups yet — pairing is opt-in via the wizard."
        columns={[
          { key: 'correlation_key', label: 'Key' },
          { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
          { key: 'late_partner', label: 'Late', render: (r) => (r.late_partner ? 'yes' : '') },
          { key: 'enrichment_missed', label: 'Missed fields', render: (r) => (r.enrichment_missed ? 'yes' : '') },
          { key: 'send', label: '', render: (r) => (r.status === 'HOLD' || r.status === 'AWAITING_PARTNER' ? <button type="button" className="ty-btn" onClick={() => send(r.id)}>Send now</button> : null) },
        ]}
      />
      {raw?.conflicts_by_field && <TechDetails title="Field conflicts" data={raw.conflicts_by_field} />}
      <UniSection title="Power-user merge configs" />
      <CrudPage
        {...P} helpPage="wizard_merge" icon="⚭" title="Merge configs"
        endpoint={`${SB_API}/merge-configs/`}
        createLabel="+ Merge pair"
        fields={[
          { key: 'name', label: 'Name' },
          { key: 'shopify_connection', label: 'Shopify store', type: 'select', required: true, options: (stores.rows || []).filter((s) => s.platform === 'SHOPIFY').map((s) => ({ value: s.id, label: s.store_domain })) },
          { key: 'gokwik_connection', label: 'GoKwik store', type: 'select', required: true, options: (stores.rows || []).filter((s) => s.platform === 'GOKWIK').map((s) => ({ value: s.id, label: s.store_domain })) },
          { key: 'merge_window_seconds', label: 'Window (seconds)' },
          { key: 'timeout_behavior', label: 'On timeout', type: 'select', options: [
            { value: 'SEND_PARTIAL', label: 'Send whichever side arrived' },
            { value: 'HOLD', label: 'Hold for a human' },
          ] },
          { key: 'use_shopify_order_id', label: 'Correlate on Shopify order id', type: 'check' },
          { key: 'use_checkout_token', label: 'Correlate on checkout token', type: 'check' },
          { key: 'use_composite_fallback', label: 'Composite phone+value+30min (risky)', type: 'check' },
          { key: 'is_active', label: 'Active', type: 'check' },
        ]}
        extraRowActions={(r) => ([
          { key: 'wizard', label: 'Edit in wizard', to: `/signalbridge/merge-setup?merge=${r.id}` },
        ])}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'shopify_domain', label: 'Shopify' },
          { key: 'gokwik_domain', label: 'GoKwik' },
          { key: 'timeout_behavior', label: 'Timeout' },
          { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
        ]}
      />
    </UniPageShell>
  );
}
